import { MatchmakingMessage, MatchmakingMessageType, InvitationMessageType } from '@playcastdotio/Messaging';
import { getStateStore } from '../../../stores';
import { machineIdState, profileState, steamCredentialsState } from '../../../stores/shared';
import { sendMessage } from '..';
import { isPendingInvitationCreation, clearPendingInvitationCreation } from './profile';
import { websocket } from '@playcastdotio/Messaging';
import { handleSoftKickActivation } from '../../managers/softKick';
import { getPersistentPlayerId } from '../../../utilities/persistentId';
import { sendAnalytics, PlaycastAnalyticsMatchFound } from '@playcastdotio/Analytics';
import { isElectron } from '../../..';
import { switchToCampaign, clearCampaign } from '../../managers/matchmaker';
import * as Sentry from '@sentry/react';
import { USE_MESSAGE_ROUTER } from '../../../../config/featureFlags';
import { realtimeMessageRouter } from '../../../../services/MessageRouter';

const unsubscribeFromInvitations = (invitationIds: string[]) => {
  if (invitationIds.length === 0) return;

  sendMessage(
    websocket.generators.invitation(InvitationMessageType.INVITATION_UNSUBSCRIBE, {
      invitationIds,
    }),
  );
};

export const handleMatchmakerMessage = async (message: MatchmakingMessage) => {
  console.log('Matchmaking message:', message);

  // ========================================
  // NEW PATH: Route to coordinators (Strangler-Fig Pattern)
  // ========================================
  if (USE_MESSAGE_ROUTER) {
    // Route the message directly - MessageRouter uses message.type to find handlers
    realtimeMessageRouter.route(message);
    return; // Early return - don't execute legacy code
  }

  // ========================================
  // OLD PATH: Legacy state store updates
  // ========================================
  const state = getStateStore('ipc');

  if (state.hostState.currentState.edition !== 'pavilion') {
    console.log('Matchmaker not supported in this mode');
    return;
  }

  switch (message.type) {
    case MatchmakingMessageType.MATCHMAKER_ACTIVATE_SOFT_KICK_REPLY: {
      // Handle soft kick activation - the soft kick manager will handle all the logic
      await handleSoftKickActivation();
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_STATE_UPDATE: {
      console.log('Matchmaker state update:', message);

      // Log state updates to Sentry for debugging
      Sentry.addBreadcrumb({
        category: 'matchmaking',
        message: `Matchmaker state: ${message.state}`,
        level: 'info',
        data: {
          state: message.state,
          reason: message.stateDetails?.reason,
          matchmakeable: message.stateDetails?.matchmakeable,
          invitationId: message.stateDetails?.currentMatchInvitationId,
          // Guest mode context
          guest_mode_enabled: state.hostMatchmakingState.currentState.guestAccount,
          ad_mode_enabled: state.adModeState.currentState.enabled,
          edition: state.hostState.currentState.edition,
        },
      });

      if (message.state === 'PREFERENCES_UPDATED') {
        state.matchmakerState.updateState({
          ...state.matchmakerState.currentState,
          stateDetails: {
            ...(state.matchmakerState.currentState.stateDetails as any),
            preferences: message.stateDetails?.preferences,
            timestamp: message.stateDetails?.timestamp ?? Date.now(),
          },
        });
      } else if (message.state === 'REGISTERED') {
        state.matchmakerState.updateState({
          ...state.matchmakerState.currentState,
          state: message.state,
          stateDetails: {
            ...(state.matchmakerState.currentState.stateDetails as any),
            ...message.stateDetails,
          },
        });
      } else if (message.state === 'MATCHMAKING_DISABLED') {
        // Check if this is expected or unexpected based on the reason
        const reason = message.stateDetails?.reason || '';
        const hasInvitation = !!message.stateDetails?.currentMatchInvitationId;

        // Expected reasons: user toggled off, host was matched, normal state changes
        const expectedReasons = [
          'host_not_available_matchmakeable_false', // User toggled matchmaking off
          'host_matched',
          'user_disabled_matchmaking',
        ];

        const isExpected = hasInvitation || expectedReasons.some(r => reason.includes(r));

        if (isExpected) {
          // Expected: Host was matched or user toggled matchmaking off
          console.log('[MATCHMAKING] Host removed from pool (expected):', {
            invitationId: message.stateDetails?.currentMatchInvitationId,
            reason: message.stateDetails?.reason,
          });

          // Log as breadcrumb - this is normal and expected
          Sentry.addBreadcrumb({
            category: 'matchmaking',
            message: 'Host removed from matchmaking pool (expected)',
            level: 'info',
            data: {
              event_type: 'matchmaking_disabled_expected',
              reason: message.stateDetails?.reason || 'unknown',
              invitation_id: message.stateDetails?.currentMatchInvitationId || 'none',
              machine_id: machineIdState.currentState,
              guest_mode: state.hostMatchmakingState.currentState.guestAccount,
              ad_mode: state.adModeState.currentState.enabled,
            },
          });
        } else {
          // Unexpected: Host removed for error/kicked/system issue
          console.warn('[MATCHMAKING] Host removed from pool (unexpected):', {
            reason: message.stateDetails?.reason,
            state: message.state,
          });

          // Log as warning - this is unexpected and should be investigated
          Sentry.captureMessage('[MATCHMAKING] Host removed from matchmaking pool (unexpected)', {
            level: 'warning',
            tags: {
              event_type: 'matchmaking_disabled_unexpected',
              reason: message.stateDetails?.reason || 'unknown',
              guest_mode: String(state.hostMatchmakingState.currentState.guestAccount),
              ad_mode: String(state.adModeState.currentState.enabled),
            },
            contexts: {
              matchmaking: {
                state: message.state,
                reason: message.stateDetails?.reason,
                matchmakeable: message.stateDetails?.matchmakeable,
                invitationId: message.stateDetails?.currentMatchInvitationId || 'none',
                machineId: machineIdState.currentState,
              },
              guest_mode: {
                enabled: state.hostMatchmakingState.currentState.guestAccount,
                edition: state.hostState.currentState.edition,
                adModeEnabled: state.adModeState.currentState.enabled,
                gameLaunched: state.playjectorHostModeState.currentState.gameLaunched,
                userLoggedIn: state.playjectorHostModeState.currentState.userLoggedIn,
              },
            },
          });
        }

        state.matchmakerState.updateState({
          ...state.matchmakerState.currentState,
          state: message.state,
          stateDetails: {
            ...(state.matchmakerState.currentState.stateDetails as any),
            ...message.stateDetails,
          },
        });
        state.hostMatchmakingState.updateState({
          ...state.hostMatchmakingState.currentState,
          matchmakeable: message.stateDetails?.matchmakeable ?? false,
        });
      }
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_HOST_MATCH_FOUND: {
      sendAnalytics({
        name: 'Host Matched',
        role: 'host',
        invitationId: message.invitationId,
        connectedPersistentId: getPersistentPlayerId(), // The player's persistent ID who matched to this host
        tier2Filters: ['machine'],
        source: isElectron ? 'electron' : 'browser',
      } as PlaycastAnalyticsMatchFound);

      console.log('Matchmaker host match found:', message);

      // Log match found as breadcrumb (will be converted to event when player actually joins)
      Sentry.addBreadcrumb({
        category: 'matchmaking',
        message: 'Match Found - Host matched with players',
        level: 'info',
        data: {
          event_type: 'match_found',
          invitation_id: message.invitationId,
          invitation_type: message.invitation?.type,
          player_count: message.invitation?.type === 'cafe' ? message.invitation.manifest.players?.length || 0 : 0,
          steam_creds_provided: message.steamCredentials?.length || 0,
          match_timestamp: message.matchTimestamp,
          machine_id: machineIdState.currentState,
          guest_mode: state.hostMatchmakingState.currentState.guestAccount,
          ad_mode: state.adModeState.currentState.enabled,
        },
      });

      const invitation = message.invitation;
      state.invitationStateV2.updateState({ ...invitation });

      if (message.steamCredentials && message.steamCredentials?.length > 0) {
        const steamCreds = message.steamCredentials[0];
        steamCredentialsState.updateState(steamCreds.username + ' ' + steamCreds.password);
      }
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_INVITATION_CREATED: {
      console.log('Matchmaker invitation created:', message);
      if (message.invitation) {
        // Place the invitation at the top of the list
        const dedupedInvitations = state.profileState.currentState.invitations.filter(
          (invitation) => invitation.id !== message?.invitation?.id,
        );

        // Check if we were the ones who initiated this invitation creation
        const wasInitiatedLocally = isPendingInvitationCreation();
        // Clear the flag
        clearPendingInvitationCreation();

        state.profileState.updateState({
          ...state.profileState.currentState,
          invitations: [message.invitation, ...dedupedInvitations],
          // Only select the invitation if we initiated the creation
          ...(wasInitiatedLocally ? { selectedInvitationId: message.invitation.id } : {}),
        });
      }
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_INVITATION_UPDATED: {
      console.log('Matchmaker invitation updated:', message);
      if (message.invitationId) {
        // Find the invitation and update it
        let inv = profileState.currentState.invitations.find((invitation) => invitation.id === message.invitationId);
        if (inv && inv.type === 'cafe') {
          if (message.invitation?.invalid) {
            // Remove the invitation from the list and unsubscribe
            const index = profileState.currentState.invitations.findIndex((invitation) => invitation.id === message.invitationId);
            const removedInvitation = profileState.currentState.invitations[index];
            profileState.updateState({
              ...profileState.currentState,
              invitations: [
                ...profileState.currentState.invitations.slice(0, index),
                ...profileState.currentState.invitations.slice(index + 1),
              ],
            });
            // Unsubscribe from the removed invitation
            unsubscribeFromInvitations([removedInvitation.id]);
          } else {
            inv = {
              ...inv,
              ...message.invitation,
              manifest: {
                ...inv.manifest,
                ...message.invitation?.manifest,
              },
            } as typeof inv;
            const index = profileState.currentState.invitations.findIndex((invitation) => invitation.id === message.invitationId);
            profileState.updateState({
              ...profileState.currentState,
              invitations: [
                ...profileState.currentState.invitations.slice(0, index),
                inv,
                ...profileState.currentState.invitations.slice(index + 1),
              ],
            });
          }
        }
        // Also check whether this invitation is the one we are matched to.
        if (message.invitationId === state.invitationStateV2.currentState.id) {
          state.invitationStateV2.updateState({
            ...state.invitationStateV2.currentState,
            ...message.invitation,
          });
        }
      }
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_MATCH_FOUND: {
      console.log('Matchmaker match found:', message);
      profileState.updateState({
        ...profileState.currentState,
        inQueue: undefined,
      });
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_QUEUE_STATUS: {
      console.log('Matchmaker queue status:', message);
      profileState.updateState({
        ...profileState.currentState,
        inQueue: message.invitationId,
      });
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_MATCH_READY: {
      console.log('Matchmaker match ready:', message);
      profileState.updateState({
        ...profileState.currentState,
        inQueue: undefined,
        matchFoundId: message.invitationId,
        matchBroadcastId: message.broadcastId,
      });
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_CAMPAIGN_SWITCH: {
      console.log('[Campaign Switch] Received request to switch to:', message.campaignId || '(clear)');
      const { campaignId, reason } = message;

      // Empty campaignId means "clear campaign" (go idle)
      // This happens when the current campaign is disabled
      if (!campaignId || campaignId === '') {
        await clearCampaign(reason);
      } else {
        // Perform the campaign switch using existing PlayjectorService system
        await switchToCampaign(campaignId, reason);
      }
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_ERROR: {
      console.error('Matchmaker error:', message);

      // Log all matchmaker errors to Sentry
      Sentry.captureMessage(`[MATCHMAKING] Error from API: ${message.error.code}`, {
        level: 'error',
        tags: {
          event_type: 'matchmaking_error',
          error_code: message.error.code,
          invitation_id: message.invitationId || 'none',
          guest_mode: String(state.hostMatchmakingState.currentState.guestAccount),
          ad_mode: String(state.adModeState.currentState.enabled),
        },
        contexts: {
          error: {
            code: message.error.code,
            message: message.error.message,
            invitationId: message.invitationId,
            machineId: machineIdState.currentState,
          },
          guest_mode: {
            enabled: state.hostMatchmakingState.currentState.guestAccount,
            edition: state.hostState.currentState.edition,
            adModeEnabled: state.adModeState.currentState.enabled,
            gameLaunched: state.playjectorHostModeState.currentState.gameLaunched,
            userLoggedIn: state.playjectorHostModeState.currentState.userLoggedIn,
            matchmakeable: state.hostMatchmakingState.currentState.matchmakeable,
          },
        },
      });
      break;
    }
    case MatchmakingMessageType.MATCHMAKER_MATCH_FAILED: {
      console.error('Matchmaker match failed:', message);

      // Log match failures to Sentry
      Sentry.captureMessage(`[MATCHMAKING] Match Failed: ${message.reason}`, {
        level: 'error',
        tags: {
          event_type: 'match_failed',
          reason: message.reason,
          can_retry: String(message.canRetry),
          invitation_id: message.invitationId,
          guest_mode: String(state.hostMatchmakingState.currentState.guestAccount),
          ad_mode: String(state.adModeState.currentState.enabled),
        },
        contexts: {
          match_failure: {
            invitationId: message.invitationId,
            reason: message.reason,
            canRetry: message.canRetry,
            machineId: machineIdState.currentState,
          },
          guest_mode: {
            enabled: state.hostMatchmakingState.currentState.guestAccount,
            edition: state.hostState.currentState.edition,
            adModeEnabled: state.adModeState.currentState.enabled,
            gameLaunched: state.playjectorHostModeState.currentState.gameLaunched,
            userLoggedIn: state.playjectorHostModeState.currentState.userLoggedIn,
            matchmakeable: state.hostMatchmakingState.currentState.matchmakeable,
          },
        },
      });
      break;
    }
  }
};
